import React, { useEffect, useState, useContext } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box"; // Import Box
import {
  doc,
  setDoc,
  getDoc,
  onSnapshot,
  getDocs, // Added
  collection, // Added
  query, // Added
  where, // Added
  limit, // Added
} from "firebase/firestore";
import CircularProgress from "@mui/material/CircularProgress"; // Added

import { auth, db } from "./firebase/firebase.js";

import Navbar from "./components/layout/Navbar.js";
import Login from "./components/auth/Login.js";
import Register from "./components/auth/Register.js";
import ForgotPassword from "./components/auth/ForgotPassword.js";
import Dashboard from "./components/dashboard/Dashboard.js";
import Profile from "./components/dashboard/Profile.js";
import AgentsPage from "./components/agents/AgentsPage.js";
import TodayPage from "./components/today/TodayPage.js";
import BirdsEyePage from "./components/birdsEye/BirdsEyePage.js";
import PendingProfileUpdates from "./components/profile/PendingProfileUpdates.jsx";
import ProtectedRoute from "./routes/ProtectedRoute.js";
import BreathingPage from "./components/breathing/BreathingPage.js";
import TermsOfService from "./components/legal/TermsOfService.js";
import PrivacyPolicy from "./components/legal/PrivacyPolicy.js";
import ResearchConsent from "./components/legal/ResearchConsent.js";
import VerifyEmail from "./components/auth/VerifyEmail.js";
import TherapistPage from "./components/therapist/TherapistPage.js";
import PersonalTrainerPage from "./components/trainer/PersonalTrainerPage.js";
import { ImageButtonsProvider } from "./context/ImageButtonsContext";
import { TimerProvider } from "./context/TimerContext";
import { ChatVisibilityProvider } from "./context/ChatVisibilityContext";
import { PointsVisibilityProvider } from "./context/PointsVisibilityContext"; // Added
import { NodeButtonsProvider } from "./context/NodeButtonsContext";
import { ImageVisibilityProvider } from "./context/ImageVisibilityContext";

import {
  TutorialProvider,
  TutorialContext,
} from "./tutorial/TutorialContext.js"; // Import TutorialProvider and TutorialContext
import { Snackbar, Button as MuiButton } from "@mui/material"; // Renamed Button to MuiButton to avoid conflict

import { initMessaging } from "./firebase/messaging.js";
import getTheme from "./theme/theme.js";
// import DirectShepherdTest from "./components/debug/DirectShepherdTest.js"; // Removed import
// import { ShepherdTourProvider } from 'react-shepherd'; // Reverted
// import { getTourOptions } from './tutorial/ShepherdConfig.js'; // Reverted

import { useLocation } from "react-router-dom"; // Ensure useLocation is imported

// Tutorial Prompt Component
const TutorialPrompt = () => {
  const tutorialContext = useContext(TutorialContext);
  const {
    startTutorial,
    tutorialDisabled,
    markTutorialDisabled,
    isTutorialActive,
  } = tutorialContext;
  const [isPromptVisible, setIsPromptVisible] = useState(true); // Local state for prompt visibility
  const location = useLocation();

  useEffect(() => {
    // This effect now primarily decides if the prompt *could* be shown based on tutorialDisabled.
    // The actual showing is controlled by isPromptVisible.
    // If tutorial gets disabled (e.g. from another browser tab), and prompt is visible, it will hide.
    if (tutorialDisabled) {
      setIsPromptVisible(false);
    }
    // We don't want to automatically re-show the prompt if tutorialDisabled becomes false again
    // without a user action like "Reset Tutorial".
  }, [tutorialDisabled]);

  const handleStartTour = () => {
    startTutorial(location.pathname);
    setIsPromptVisible(false);
  };

  const handleMaybeLater = () => {
    setIsPromptVisible(false);
  };

  const handleDontShowPrompt = () => {
    // Renamed for clarity
    markTutorialDisabled();
    setIsPromptVisible(false);
  };

  // Conditions for showing the Snackbar
  const openSnackbar =
    isPromptVisible && !tutorialDisabled && !isTutorialActive;

  if (!openSnackbar) {
    // If conditions not met, render nothing
    return null;
  }

  return (
    <Snackbar
      open={openSnackbar} // Controlled by combined state
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      message="New here? Take a quick tour!"
      action={
        <>
          <MuiButton type="button" color="secondary" size="small" onClick={handleStartTour}>
            Start Tour
          </MuiButton>
          <MuiButton color="inherit" size="small" onClick={handleMaybeLater}>
            Maybe Later
          </MuiButton>
          <MuiButton
            color="inherit"
            size="small"
            onClick={handleDontShowPrompt}
          >
            {" "}
            {/* Updated handler name */}
            Don't Show
          </MuiButton>
        </>
      }
    />
  );
};

function App() {
  /* login state */
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUid, setCurrentUid] = useState(null); // Added to store UID

  /* theme toggle */
  const getInitialMode = () => {
    const fromLS = localStorage.getItem("themeMode");
    if (fromLS) return fromLS;
    const u = auth.currentUser;
    // synchronous check – the user may not be logged in yet
    return u?.uid && u.reload && u.reload()
      ? "dark" // arbitrary default for signed‑in users
      : "light";
  };
  const [mode, setMode] = useState(getInitialMode);
  const theme = React.useMemo(() => getTheme(mode), [mode]);
  // const currentTourOptions = getTourOptions(mode); // Reverted

  const toggleColorMode = () => {
    setMode((prev) => {
      const next = prev === "light" ? "dark" : "light";
      localStorage.setItem("themeMode", next); // device persistence
      const u = auth.currentUser;
      if (u) {
        // cross‑device sync
        setDoc(doc(db, "users", u.uid), { themeMode: next }, { merge: true });
      }
      return next;
    });
  };

  useEffect(() => {
    let unsubThemeSnapshot = () => {}; // Initialize with a no-op function
    const unsubIsAuthenticated = auth.onAuthStateChanged(async (user) => {
      setIsAuthenticated(!!user);
      setCurrentUid(user ? user.uid : null);
      if (user) {
        initMessaging(); // single call after sign‑in
        const userDocRef = doc(db, `users/${user.uid}`);
        const userDocSnap = await getDoc(userDocRef);
        const theme = userDocSnap.data()?.themeMode;
        if (theme) setMode(theme);

        // Clean up previous theme listener before starting a new one
        unsubThemeSnapshot();
        unsubThemeSnapshot = onSnapshot(userDocRef, (snap) => { // Assign to the outer scope variable
          const newTheme = snap.data()?.themeMode;
          setMode((prev) => (newTheme && newTheme !== prev ? newTheme : prev));
        });
      } else {
        // User logged out, clean up theme listener
        unsubThemeSnapshot();
        unsubThemeSnapshot = () => {}; // Reset to no-op
      }
    });

    return () => {
      unsubIsAuthenticated();
      unsubThemeSnapshot(); // Clean up theme listener on App unmount
    };
  }, [auth, db]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ImageButtonsProvider>
        <TimerProvider>
          <ChatVisibilityProvider>
            <PointsVisibilityProvider>
              <NodeButtonsProvider>
                <ImageVisibilityProvider>
              {/* <ShepherdTourProvider config={currentTourOptions}> */} {/* Reverted */}
              <TutorialProvider>
                <Router>
                  <Box
                    sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100vh",
                  }}
                >
                  <Navbar mode={mode} toggleColorMode={toggleColorMode} />
                  <Box
                    component="main"
                    sx={{
                      flexGrow: 1,
                      overflowY: "auto",
                      p: 0,
                    }}
                  >
                    <Routes>
                      <Route
                        path="/login"
                        element={
                          !isAuthenticated ? (
                            <Login />
                          ) : (
                            <Navigate to="/dashboard" />
                          )
                        }
                      />
                      <Route
                        path="/register"
                        element={
                          !isAuthenticated ? (
                            <Register />
                          ) : (
                            <Navigate to="/dashboard" />
                          )
                        }
                      />
                      <Route
                        path="/forgot-password"
                        element={
                          !isAuthenticated ? (
                            <ForgotPassword />
                          ) : (
                            <Navigate to="/dashboard" />
                          )
                        }
                      />
                      <Route path="/verify-email" element={<VerifyEmail />} />
                      <Route
                        path="/dashboard"
                        element={<ProtectedRoute element={Dashboard} />}
                      />
                      <Route
                        path="/birds-eye"
                        element={<ProtectedRoute element={BirdsEyePage} />}
                      />
                      <Route
                        path="/profile"
                        element={<ProtectedRoute element={Profile} />}
                      />
                      <Route
                        path="/profile/updates"
                        element={
                          <ProtectedRoute element={PendingProfileUpdates} />
                        }
                      />
                      <Route
                        path="/goals"
                        element={<Navigate to="/dashboard" />}
                      />
                      <Route
                        path="/agents"
                        element={<ProtectedRoute element={AgentsPage} />}
                      />
                      <Route
                        path="/terms-of-service"
                        element={<TermsOfService />}
                      />
                      <Route
                        path="/privacy-policy"
                        element={<PrivacyPolicy />}
                      />
                      <Route
                        path="/research-consent"
                        element={<ResearchConsent />}
                      />
                      <Route
                        path="/today"
                        element={<ProtectedRoute element={TodayPage} />}
                      />
                      <Route
                        path="/therapist"
                        element={<ProtectedRoute element={TherapistPage} />}
                      />
                      <Route
                        path="/trainer"
                        element={<ProtectedRoute element={PersonalTrainerPage} />}
                      />
                      <Route
                        path="/breathing"
                        element={<ProtectedRoute element={BreathingPage} />}
                      />
                      <Route
                        path="/"
                        element={
                          !isAuthenticated ? (
                            <Login />
                          ) : (
                            <RootRedirector userId={currentUid} />
                          )
                        }
                      />
                    </Routes>
                  </Box>
                  {isAuthenticated && <TutorialPrompt />}
                  {/* <DirectShepherdTest /> */} {/* Removed component */}
                  </Box>
                </Router>
              </TutorialProvider>
              {/* </ShepherdTourProvider> */} {/* Reverted */}
                </ImageVisibilityProvider>
            </NodeButtonsProvider>
            </PointsVisibilityProvider>
          </ChatVisibilityProvider>
        </TimerProvider>
      </ImageButtonsProvider>
    </ThemeProvider>
  );
}

// Define RootRedirector component
const RootRedirector = ({ userId }) => {
  const [redirectPath, setRedirectPath] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set loading to true when userId changes and is valid
    if (userId) {
      setLoading(true);
    } else if (userId === null) {
      // Explicitly handle null userId (user logged out or not yet known)
      setRedirectPath("/login");
      setLoading(false);
      return;
    } else {
      // userId is undefined (still loading from auth.onAuthStateChanged)
      // We keep loading true and wait for userId to be set.
      setLoading(true);
      return;
    }

    const checkForUrgentTasks = async () => {
      try {
        const progressCollectionRef = collection(db, "progress");
        const urgentCandidatesQuery = query(
          progressCollectionRef,
          where("uid", "==", userId),
          limit(25)
        );
        const querySnapshot = await getDocs(urgentCandidatesQuery);
        const hasUrgentTasks = querySnapshot.docs.some(
          (docSnapshot) => docSnapshot.data()?.isUrgent
        );
        if (hasUrgentTasks) {
          setRedirectPath("/today");
        } else {
          setRedirectPath("/dashboard");
        }
      } catch (error) {
        console.error("Error checking for urgent tasks:", error);
        setRedirectPath("/dashboard"); // Default to dashboard on error
      } finally {
        setLoading(false);
      }
    };

    checkForUrgentTasks();
  }, [userId]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 70px)", // Adjust height considering Navbar
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  // Fallback if redirectPath is somehow not set, though ideally covered by logic.
  // This could redirect to login or a generic error page.
  return <Navigate to="/login" replace />;
};

export default App;
